import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

const ScoreRing = ({ label, score, total, percentage, size = 64, thickness = 4, highlight, sub }) => {
    const rawPct = percentage != null ? percentage : (total > 0 ? Math.round((score / total) * 100) : 0);
    const pct = Math.min(100, rawPct);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={100} size={size} thickness={thickness}
                    sx={{ color: 'action.hover', position: 'absolute' }} />
                <CircularProgress variant="determinate" value={pct} size={size} thickness={thickness}
                    sx={{ color: highlight ? 'warning.main' : pct >= 70 ? 'success.main' : pct >= 40 ? 'info.main' : 'error.main' }} />
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: size * 0.22 }}>
                        {percentage != null ? score : `${pct}%`}
                    </Typography>
                </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            {total != null && (
                <Typography variant="caption" fontWeight={500}>{score}/{total}</Typography>
            )}
            {sub && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{sub}</Typography>
            )}
        </Box>
    );
};

const PendingRing = ({ label, text = 'Pending', size = 64, thickness = 4 }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={size} thickness={thickness} sx={{ color: 'action.disabled' }} />
        </Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="text.secondary">{text}</Typography>
    </Box>
);

/**
 * Props:
 *   sectionScores — [{ name, score, total, pending, adminPendingText }]
 *   compositeScore, scaledScore, isAdmin
 *   scaledLabel, scaledMax — what scaled_score means for THIS test (see scoreScale.js). The
 *     column holds AP 1-5 on AP tests and an ACT 1-36 composite on ACT ones, so both the label
 *     and the ring's denominator have to come from the test's scoring profile rather than being
 *     assumed. Defaults keep AP tests rendering as they always did.
 *   alternates — { name: score } of the profile's other composites, e.g. ACT's four-way average
 *     including Science. Rendered plain beside the highlighted headline, because the headline is
 *     the official composite and the alternate is context. A null value means the alternate
 *     couldn't be built (ACT Science is optional), so it simply doesn't appear.
 *   breakdown — { correct, blank, wrong, max } for points-per-answer tests (AMC), where the
 *     headline is a sum rather than a curve: the ring then shows "121.5/150" and the split
 *     underneath, since a blank and a wrong answer are worth different amounts.
 */
// with_science -> "With Science"
const altLabel = (key) => key.split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const ScoreRings = ({ sectionScores = [], compositeScore, scaledScore, isAdmin,
    scaledLabel = "AP Score", scaledMax = 5, alternates = null, breakdown = null }) => {
    const totalScore = sectionScores.reduce((s, sec) => s + (sec.score || 0), 0);
    const totalMax = sectionScores.reduce((s, sec) => s + (sec.total || 0), 0);
    const anyPending = sectionScores.some(s => s.pending);

    return (
        <Box sx={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', py: 3, px: 4 }}>
            {sectionScores.map((sec, idx) => (
                sec.pending
                    ? <PendingRing key={idx} label={sec.name} text={isAdmin && sec.adminPendingText ? sec.adminPendingText : 'Pending'} />
                    : <ScoreRing key={idx} label={sec.name} score={sec.score} total={sec.total} />
            ))}
            {sectionScores.length > 1 && (
                anyPending
                    ? <PendingRing label="Total" text="Pending" />
                    : <ScoreRing label="Total" score={totalScore} total={totalMax} />
            )}
            {isAdmin && compositeScore != null && (
                <ScoreRing label="Composite" score={compositeScore} total={null}
                    percentage={totalMax > 0 ? Math.round((compositeScore / totalMax) * 100) : 0}
                />
            )}
            {scaledScore != null && (
                <ScoreRing label={scaledLabel} score={scaledScore}
                    total={breakdown?.max ?? null}
                    sub={breakdown ? `${breakdown.correct} correct · ${breakdown.blank} blank · ${breakdown.wrong} wrong` : null}
                    percentage={scaledMax > 0 ? Math.min(100, Math.round((scaledScore / scaledMax) * 100)) : 0} highlight />
            )}
            {scaledScore != null && Object.entries(alternates || {})
                .filter(([name, value]) => name !== "composite" && typeof value === "number")
                .map(([name, value]) => (
                    <ScoreRing key={name} label={altLabel(name)} score={value} total={null}
                        percentage={scaledMax > 0 ? Math.min(100, Math.round((value / scaledMax) * 100)) : 0} />
                ))}
        </Box>
    );
};

export default ScoreRings;
